/**
 * Refund / dispute-resolution constants.
 * Mirrors the backend enums in basquet-backend src/constants (REFUND_TYPE,
 * DISPUTE_RESOLUTION_ACTION) and the merchant dispute-resolution screens.
 */

/** @typedef {'STANDARD_FULL' | 'UNDERPAYMENT_FULL' | 'OVERPAYMENT_EXCESS' | 'OVERPAYMENT_FULL'} RefundType */
const REFUND_TYPE = {
  STANDARD_FULL: 'STANDARD_FULL',
  UNDERPAYMENT_FULL: 'UNDERPAYMENT_FULL',
  OVERPAYMENT_EXCESS: 'OVERPAYMENT_EXCESS',
  OVERPAYMENT_FULL: 'OVERPAYMENT_FULL'
};

const DISPUTE_RESOLUTION_ACTION = {
  ACCEPT_UNDERPAYMENT: 'ACCEPT_UNDERPAYMENT',
  ACCEPT_OVERPAYMENT: 'ACCEPT_OVERPAYMENT'
};

const DISPUTE_TYPE = {
  OVERPAID: 'OVERPAID',
  UNDERPAID: 'UNDERPAID',
  LATE_PAYMENT: 'LATE_PAYMENT'
};

/**
 * Resolution options per chooser. `kind` tells the orchestrator whether the
 * option creates a refund (preview + create-refund) or accepts the dispute
 * (accept endpoint). `value` is the backend enum sent on the wire.
 * `detailHeader`/`acceptHeader` drive the modal title for that branch's screen;
 * `summaryType` is the "Refund type" label on the summary. `acceptHasDetail`
 * inserts a confirmation screen before 2FA on the accept branch.
 */
const OVERPAYMENT_RESOLUTION_OPTIONS = [
  {
    id: 'refund_excess',
    kind: 'refund',
    value: REFUND_TYPE.OVERPAYMENT_EXCESS,
    label: 'Yes, refund excess',
    // description is built at render time so the excess amount can be injected.
    detailHeader: { name: 'Refund', cursive: 'excess' },
    summaryType: 'Excess refund'
  },
  {
    id: 'accept_excess',
    kind: 'accept',
    value: DISPUTE_RESOLUTION_ACTION.ACCEPT_OVERPAYMENT,
    label: 'Accept excess',
    description:
      'The settlement value is calculated using the current exchange rate at acceptance time.'
  },
  {
    id: 'full_refund',
    kind: 'refund',
    value: REFUND_TYPE.OVERPAYMENT_FULL,
    label: 'Full refund',
    description:
      'No settlement will be made to your account. We will make a full refund to the customer.',
    detailHeader: { name: 'Full', cursive: 'refund' },
    summaryType: 'Full refund'
  }
];

const UNDERPAYMENT_RESOLUTION_OPTIONS = [
  {
    id: 'refund_underpayment',
    kind: 'refund',
    value: REFUND_TYPE.UNDERPAYMENT_FULL,
    label: 'Refund underpayment',
    description:
      'This returns the exact amount received to your customer and they will bear the refund fee.',
    detailHeader: { name: 'Refund', cursive: 'underpayment' },
    summaryType: 'Full refund'
  },
  {
    id: 'accept_underpayment',
    kind: 'accept',
    value: DISPUTE_RESOLUTION_ACTION.ACCEPT_UNDERPAYMENT,
    label: 'Accept underpayment',
    description:
      'The settlement value is calculated using the current exchange rate at acceptance time and settled in your account.',
    acceptHasDetail: true,
    acceptHeader: { name: 'Accept', cursive: 'underpayment' }
  }
];

/** Per dispute-type chooser configuration (title, prompt, options). */
const RESOLUTION_CONFIG = {
  [DISPUTE_TYPE.OVERPAID]: {
    title: { name: 'Overpayment', cursive: 'dispute' },
    prompt: 'Do you want to pay back the excess amount?',
    options: OVERPAYMENT_RESOLUTION_OPTIONS
  },
  [DISPUTE_TYPE.UNDERPAID]: {
    title: { name: 'Underpayment', cursive: 'dispute' },
    prompt: 'How do you want to resolve this underpayment?',
    options: UNDERPAYMENT_RESOLUTION_OPTIONS
  }
};

/** A dispute can only be resolved while it is still open (status DISPUTE). */
const RESOLVABLE_DISPUTE_STATUS = 'DISPUTE';

/** Maps raw backend dispute statuses to merchant-facing labels. */
const DISPUTE_STATUS_LABELS = {
  DISPUTE: 'Dispute',
  PENDING: 'Pending',
  ALLOWED: 'Accepted',
  REFUND_SUCCESSFUL: 'Refunded',
  REFUND_FAILED: 'Refund failed'
};

export {
  DISPUTE_RESOLUTION_ACTION,
  DISPUTE_STATUS_LABELS,
  DISPUTE_TYPE,
  OVERPAYMENT_RESOLUTION_OPTIONS,
  REFUND_TYPE,
  RESOLUTION_CONFIG,
  RESOLVABLE_DISPUTE_STATUS,
  UNDERPAYMENT_RESOLUTION_OPTIONS
};
